import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle2, Undo2 } from 'lucide-react'
import {
  Button, Card, CardBody, CardHeader, ErrorState, Identifier, LoadingState, useLocale,
} from '@hk/ui'
import {
  fetchInspection, formatCount, formatMinutes, formatTime, nameIn, passInspection,
  pluralise, sendBackInspection, translateError,
  type InspectionLine, type InspectionView,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   W-51 — INSPECTING A ROOM, AT THE DESK.  Sprint 65.

   ── THE SAME DATA AS M-13 AND A DIFFERENT SCREEN ───────────────────────────────
   The handset has drawn `InspectionView` since Sprint 46. This is the first time the desk
   has, and the two are NOT one screen at two widths — the same judgement W-21 made about
   M-03 in Sprint 62.

   M-13 is held in one hand at a door, with the room in front of the supervisor: it is
   ordered by the checklist, because he is looking at the bathroom while he reads the
   bathroom line. **THIS IS READ AFTER THE FACT**, by a manager clearing a backlog from a
   desk, who cannot see the room at all. So the figures come first and the sentence that
   matters — *this is the same man's second bathroom this week* — is not at the bottom of a
   scroll; it is beside the line it is about, where somebody skimming will meet it.

   ── PASS IS ONE PRESS AND SEND BACK IS TWO ─────────────────────────────────────
   M-13's rule, kept here because it is about people and not about a screen size. Most
   rooms pass, and a screen that asked for a reason before letting somebody say yes is a
   screen they stop opening. Sending a room back needs a sentence, because somebody is
   telling a man to clean a room again.

   ── AND IT DOES NOT TOUCH WHAT THE ATTENDANT SAID ──────────────────────────────
   His answers stand as his statement; what the inspector found is recorded beside them.
   Two people looked at the same bathroom and disagreed, and both halves of that are worth
   keeping.
   ========================================================================= */

export function InspectRoom({
  taskId, propertyId, onClose,
}: {
  taskId: string
  propertyId: string
  onClose: () => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const queryClient = useQueryClient()

  const [view, setView] = useState<InspectionView>()
  const [marked, setMarked] = useState<Record<string, boolean>>({})
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<'passed' | 'sent'>()
  const [error, setError] = useState<unknown>()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setView(await fetchInspection(taskId))
      setError(undefined)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [taskId])

  useEffect(() => { void load() }, [load])

  const markedIds = Object.entries(marked).filter(([, on]) => on).map(([id]) => id)

  /* **EVERY BOARD THAT COULD BE WRONG A SECOND LATER.** A verdict moves the room out of
     the queue AND changes its housekeeping state, so the queue, the room board and the
     operations dashboard are all stale the instant this succeeds. */
  const settled = () => {
    void queryClient.invalidateQueries({ queryKey: ['inspection-queue', propertyId] })
    void queryClient.invalidateQueries({ queryKey: ['room-board', propertyId] })
    void queryClient.invalidateQueries({ queryKey: ['operations', propertyId] })
  }

  const act = async (what: 'pass' | 'send') => {
    setBusy(true)
    setError(undefined)
    try {
      if (what === 'pass') await passInspection(taskId)
      else await sendBackInspection(taskId, reason.trim(),
        markedIds.map((checklistItemId) => ({ checklistItemId })))
      setDone(what === 'pass' ? 'passed' : 'sent')
      settled()
      await load()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  if (loading && !view) return <LoadingState />

  if (!view) {
    return (
      <ErrorState
        body={translateError(error, locale).message}
        onRetry={() => void load()}
      />
    )
  }

  const barred = view.cannotInspect !== null

  return (
    <div className="space-y-4 p-4">
      <header className="flex flex-wrap items-center gap-3">
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          <ArrowLeft className="size-3.5 rtl:rotate-180" aria-hidden />
          {t.iqBack}
        </Button>

        <h1 className="text-lg font-semibold">
          {t.room} <Identifier className="text-lg">{view.roomNumber}</Identifier>
        </h1>

        <span className="text-sm text-muted-foreground">
          {nameIn(locale, view.roomTypeName, view.roomTypeNameAr)}
          {' · '}
          {nameIn(locale, view.taskTypeName, view.taskTypeNameAr)}
        </span>

        <span className="ms-auto text-sm text-muted-foreground">
          {fill(t.iqHandedInBy, {
            name: nameIn(locale, view.attendantName, view.attendantNameAr),
            time: view.submittedAtUtc
              ? formatTime(new Date(view.submittedAtUtc), locale) : '—',
          })}
        </span>
      </header>

      {/* **SAID BEFORE HE STARTS MARKING LINES, AND SAID ACCURATELY.** Drawing the buttons
          and refusing him afterwards wastes his time and teaches him to distrust the
          screen — and naming the wrong reason does the same damage twice as fast (#144). */}
      {barred && (
        <p role="status" className="rounded-hk border border-border bg-muted/40 px-3 py-2 text-sm">
          {t.iqCannot[view.cannotInspect!]}
        </p>
      )}

      {done !== undefined && (
        <p role="status" className="rounded-hk border border-border bg-muted/40 px-3 py-2 text-sm">
          {done === 'passed' ? t.iqPassed : t.iqSentBack}
        </p>
      )}

      {error !== undefined && (
        <p role="alert" className="text-sm" style={{ color: 'var(--hk-hk-dirty-text)' }}>
          {String(translateError(error, locale).message)}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader
            title={t.iqTheList}
            actions={
              <span className="tnum text-[12px] text-muted-fg">
                {fill(t.iqAnsweredOf, {
                  answered: formatCount(view.itemsAnswered, locale),
                  total: formatCount(view.itemsTotal, locale),
                })}
              </span>
            }
          />
          <CardBody className="space-y-1.5">
            {view.lines.length === 0 && (
              <p className="text-sm text-muted-foreground">{t.iqNoChecklist}</p>
            )}
            {view.lines.map((line) => (
              <Line
                key={line.checklistItemId}
                line={line}
                marked={marked[line.checklistItemId] === true}
                disabled={barred || done !== undefined}
                onToggle={() => setMarked((all) => ({
                  ...all, [line.checklistItemId]: !all[line.checklistItemId],
                }))}
              />
            ))}
          </CardBody>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title={t.iqFigures} />
            <CardBody className="space-y-1.5 text-sm">
              <Figure label={t.iqWaiting} value={formatMinutes(view.waitingMinutes, locale)} />
              <Figure
                label={t.iqTaken}
                /* **NULL IS NOT ZERO.** A room recovered from a handset that never
                   recorded a start took an unknown time, and "0m against a standard of
                   25" is an accusation the data does not support. */
                value={view.takenMinutes === null
                  ? t.iqNotRecorded
                  : formatMinutes(view.takenMinutes, locale)}
                note={view.standardMinutes === null ? undefined
                  : fill(t.iqAgainst, {
                    minutes: formatMinutes(view.standardMinutes, locale),
                  })}
              />
            </CardBody>
          </Card>

          {!barred && !done && (
            <Card>
              <CardHeader title={t.iqVerdict} />
              <CardBody className="space-y-2">
                {markedIds.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    {pluralise(t.iqMarked, markedIds.length, locale,
                      formatCount(markedIds.length, locale))}
                  </p>
                )}

                {/* THE BOX IS ALWAYS THERE rather than appearing once a line is marked: a
                    room can be sent back for something that is not on the list at all, and
                    a field that materialised under somebody's cursor is a field they lose
                    what they were typing in. */}
                <label className="block text-sm" htmlFor="inspect-reason">
                  {t.iqWhyBack}
                </label>
                <textarea
                  id="inspect-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  dir="auto"
                  rows={3}
                  className="w-full rounded-hk border border-border bg-background p-2 text-[13px]"
                />

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy || reason.trim() === ''}
                    onClick={() => void act('send')}
                  >
                    <Undo2 className="size-3.5" aria-hidden />
                    {t.iqSendBack}
                  </Button>

                  <Button type="button" size="sm" disabled={busy} onClick={() => void act('pass')}>
                    <CheckCircle2 className="size-3.5" aria-hidden />
                    {t.iqPass}
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

function Figure(
  { label, value, note }: { label: string; value: string; note?: string },
) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="tnum font-medium">
        {value}
        {note && <span className="ms-1.5 text-[12px] font-normal text-muted-foreground">{note}</span>}
      </span>
    </div>
  )
}

/**
 * One checklist line, with **Fail** beside it and the sentence that is the point of the
 * whole screen underneath.
 */
function Line({
  line, marked, disabled, onToggle,
}: {
  line: InspectionLine
  marked: boolean
  disabled: boolean
  onToggle: () => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  return (
    <article className={`rounded-hk border p-2.5 ${
      marked
        ? 'border-[#fca5a5] bg-[#fef2f2] dark:border-[#7f1d1d] dark:bg-[#2a0f0f]'
        : 'border-border bg-background'}`}
    >
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-medium">
            <bdi>{nameIn(locale, line.label, line.labelAr)}</bdi>
            {line.isMandatory && <span className="ms-1 text-muted-foreground">*</span>}
          </span>

          <span className="mt-0.5 block text-[11.5px] text-muted-foreground">
            {line.isException
              ? t.iqHeCouldNot
              : line.isAnswered ? t.iqHeDidIt : t.iqNotAnswered}
          </span>

          {/* HIS OWN WORDS WHERE HE MARKED AN EXCEPTION. Never translated, never
              re-flowed — and this is the line a manager is deciding against. */}
          {line.note && (
            <span className="mt-0.5 block text-[12px]" dir="auto" style={{ unicodeBidi: 'isolate' }}>
              “{line.note}”
            </span>
          )}

          {/* **THE SENTENCE THE WHOLE SCREEN IS FOR**, and it is here rather than at the
              bottom because a manager skimming ten lines will meet it beside the line it
              is about. Drawn only when it is not zero, which is almost every line of
              almost every room — an always-present "0 prior failures" is noise that
              buries the one that matters. */}
          {line.priorFailuresThisWeek > 0 && (
            <span className="mt-0.5 block text-[12px] font-medium"
                  style={{ color: 'var(--hk-hk-dirty-text)' }}>
              {pluralise(t.iqPriorFailures, line.priorFailuresThisWeek, locale,
                formatCount(line.priorFailuresThisWeek, locale))}
            </span>
          )}
        </span>

        <button
          type="button"
          disabled={disabled}
          onClick={onToggle}
          aria-pressed={marked}
          className={`shrink-0 rounded-hk border px-2.5 py-1 text-[12px] cursor-pointer
                      disabled:opacity-40 ${
            marked
              ? 'border-[#b91c1c] bg-[#b91c1c] text-white'
              : 'border-border hover:bg-muted'}`}
        >
          {t.iqFail}
        </button>
      </div>
    </article>
  )
}
